<template>
  <v-text-field
      rounded="lg"
      :error-messages="error"
      :label="translation(translationList.house_identifier, 'Cod de identificare')"
      v-model="houseIdentifier"
      name="house_identifier"
      :rules="houseIdentifierValidationRules"
      @input="onInput"
      variant="outlined"
      bg-color="surface"
      :tile="true"
  >
    <template v-slot:append-inner>
      <v-tooltip location="top" open-delay="500" open-on-click open-on-hover>
        <template v-slot:activator="{ props }">
           <v-icon v-bind="props" class="cursor-pointer" aria-label="info">mdi-information</v-icon>
        </template>
        <div>{{ translation(translationList.house_identifier_helper, 'Numar carte funciara / Cod unic cadastru') }}</div>
      </v-tooltip>
    </template>
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: String as () => string | null,
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const houseIdentifier = ref(modelValue.value ? modelValue.value : '');
    const error = ref([]);

    watch(modelValue, (newValue) => {
      if (newValue) {
        houseIdentifier.value = newValue;
      }
    });

    watch(houseIdentifier, (newValue) => {
      const valueToValidate = newValue || '';
      const isValid = validateHouseIdentifier(valueToValidate);
      emit('update:modelValue', valueToValidate);
      emit('validation-house-status', { field: 'identifier', isValid });
    });

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const houseIdentifierValidationRules = [
      (v: string) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: string) => /^[a-zA-Z0-9]+$/i.test(v) || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
    ];

    function validateHouseIdentifier(value: string, showErrors?: boolean): boolean {
      error.value = [];
      if (showErrors) {
        const errors = houseIdentifierValidationRules
            .map(rule => rule(value))
            .filter(result => result !== true) as string[];
        if (errors && errors.length > 0) {
          error.value = errors;
          return false;
        }
      }
      return houseIdentifierValidationRules.every(rule => rule(value) === true);
    }

    const onInput = function (event: Event) {
      const input = event.target as HTMLInputElement;
      houseIdentifier.value = input.value.toUpperCase();
    }

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      const isValid = validateHouseIdentifier(houseIdentifier.value);
      emit('validation-house-status', {field: `identifier`, isValid});

      // Listen for validate event
      window.addEventListener('validate-house', (data) => {
        if (data.detail && data.detail === 'identifier') {
          error.value = [];
          const isValid = validateHouseIdentifier(houseIdentifier.value, true);
          emit('validation-house-status', { field: 'identifier', isValid });
        }
      });
    });

    return {
      houseIdentifier,
      houseIdentifierValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>

<style scoped>
</style>